import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import s from './FidelidadeConfig.module.css';
import g from './InstagramGestao.module.css';

// imagem_url vem relativa do backend (/uploads/instagram/xxx) — mesmo
// padrão de foto de produto (AbaFotos.jsx): resolve pra absoluta só pra
// exibir aqui; a Meta recebe a URL pública de verdade na hora de publicar,
// resolvida do lado do backend (nunca localhost).
const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
const urlImagem = caminho => caminho?.startsWith('/uploads/') ? `${BACKEND_URL}${caminho}` : caminho;

const CONFIG_PADRAO = { ativo: false, ig_user_id: '', username: null, facebook_page_id: '', facebook_page_nome: null };

const LABEL_STATUS = {
  rascunho: 'Rascunho', agendado: 'Agendado', publicando: 'Publicando…',
  publicado: 'Publicado', erro: 'Erro', cancelado: 'Cancelado',
};
const COR_STATUS = {
  rascunho: '#64748b', agendado: '#2563eb', publicando: '#f59e0b',
  publicado: '#16a34a', erro: '#dc2626', cancelado: '#94a3b8',
};

const fmtData = iso => iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

/**
 * Gestão de Instagram (Meta Graph API) — duas abas:
 *  - Postagens: compor (imagem + legenda), publicar agora ou agendar, ver
 *    o andamento de tudo que já foi criado por aqui.
 *  - Configuração: ID da conta Business + Access Token (mesmo padrão de
 *    "campo em branco mantém o valor salvo" já usado no Mercado Pago).
 * Publicação de verdade e o agendamento rodando sozinho ficam no backend
 * (services/instagram.js + instagramScheduler.js) — esta tela só monta a
 * fila e mostra o status.
 */
export default function InstagramGestao() {
  const [aba, setAba] = useState('postagens'); // 'postagens' | 'config'

  // ── Configuração ──
  const [config, setConfig] = useState(CONFIG_PADRAO);
  const [accessToken, setAccessToken] = useState('');
  const [tokenConfigurado, setTokenConfigurado] = useState(false);
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const [erroConfig, setErroConfig] = useState('');
  const [sucessoConfig, setSucessoConfig] = useState('');

  // ── Postagens ──
  const [posts, setPosts] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState('');

  // ── Composer ──
  const [imagemArquivo, setImagemArquivo] = useState(null);
  const [imagemPreview, setImagemPreview] = useState('');
  const [imagemUrl, setImagemUrl] = useState('');
  const [legenda, setLegenda] = useState('');
  const [tipoMidia, setTipoMidia] = useState('imagem'); // 'imagem' | 'reels'
  const [videoArquivo, setVideoArquivo] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [capaArquivo, setCapaArquivo] = useState(null);
  const [noInstagram, setNoInstagram] = useState(true);
  const [noFacebook, setNoFacebook] = useState(false);
  const [avisoPost, setAvisoPost] = useState('');
  const [agendarPara, setAgendarPara] = useState('');
  const [salvandoPost, setSalvandoPost] = useState(false);
  const [erroPost, setErroPost] = useState('');

  const carregarConfig = useCallback(() => {
    api.get('/instagram/config').then(({ data }) => {
      const { access_token_configurado, ...cfg } = data;
      setConfig({ ...CONFIG_PADRAO, ...cfg });
      setTokenConfigurado(!!access_token_configurado);
    }).catch(() => setErroConfig('Erro ao carregar configuração do Instagram.'));
  }, []);

  const carregarPosts = useCallback(() => {
    api.get('/instagram/posts', { params: filtroStatus ? { status: filtroStatus } : {} })
      .then(({ data }) => setPosts(data))
      .catch(() => {});
  }, [filtroStatus]);

  useEffect(() => { carregarConfig(); }, [carregarConfig]);
  useEffect(() => { carregarPosts(); }, [carregarPosts]);
  // Reels na fila/publicando: atualiza a lista sozinho até terminar.
  const temAndamento = posts.some(p => p.status === 'publicando' || (p.status === 'agendado' && new Date(p.agendado_para) <= new Date()));
  useEffect(() => {
    if (!temAndamento) return undefined;
    const t = setInterval(carregarPosts, 10000);
    return () => clearInterval(t);
  }, [temAndamento, carregarPosts]);

  const salvarConfig = async () => {
    setErroConfig(''); setSucessoConfig(''); setSalvandoConfig(true);
    try {
      const { data } = await api.put('/instagram/config', {
        ativo: config.ativo, ig_user_id: config.ig_user_id,
        facebook_page_id: config.facebook_page_id || '',
        access_token: accessToken || undefined,
      });
      const { access_token_configurado, ...cfg } = data;
      setConfig({ ...CONFIG_PADRAO, ...cfg });
      setTokenConfigurado(!!access_token_configurado);
      setAccessToken('');
      setSucessoConfig(`Conta @${cfg.username || cfg.ig_user_id}${cfg.facebook_page_nome ? ` e Página "${cfg.facebook_page_nome}"` : ''} validada(s) e salva(s) com sucesso!`);
      setTimeout(() => setSucessoConfig(''), 5000);
    } catch (err) {
      setErroConfig(err.response?.data?.erro || 'Erro ao salvar configuração.');
    } finally { setSalvandoConfig(false); }
  };

  const escolherImagem = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagemArquivo(file);
    setImagemUrl('');
    setImagemPreview(URL.createObjectURL(file));
  };

  const enviarImagemSeNecessario = async () => {
    if (imagemUrl) return imagemUrl;
    if (!imagemArquivo) { setErroPost('Escolha uma imagem antes de continuar.'); return null; }
    try {
      const fd = new FormData();
      fd.append('imagem', imagemArquivo);
      const { data } = await api.post('/instagram/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImagemUrl(data.imagem_url);
      return data.imagem_url;
    } catch (err) {
      setErroPost(err.response?.data?.erro || 'Erro ao enviar a imagem.');
      return null;
    }
  };

  const escolherVideo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { setErroPost('O vídeo passa de 25 MB. Exporte em 1080x1920 com bitrate menor.'); return; }
    setErroPost(''); setVideoArquivo(file); setVideoPreview(URL.createObjectURL(file));
  };

  const enviarArquivo = async (rota, campo, arquivo) => {
    const fd = new FormData();
    fd.append(campo, arquivo);
    const { data } = await api.post(rota, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  };

  const limparComposer = () => {
    setImagemArquivo(null); setImagemPreview(''); setImagemUrl('');
    setVideoArquivo(null); setVideoPreview(''); setCapaArquivo(null);
    setLegenda(''); setAgendarPara(''); setErroPost('');
  };

  const salvarPost = async (publicarAgora) => {
    setErroPost(''); setAvisoPost(''); setSalvandoPost(true);
    try {
      if (!noInstagram && !noFacebook) { setErroPost('Escolha onde publicar: Instagram, Facebook ou os dois.'); setSalvandoPost(false); return; }
      const body = { tipo_midia: tipoMidia, legenda, publicar_instagram: noInstagram, publicar_facebook: noFacebook };
      if (tipoMidia === 'reels') {
        if (!videoArquivo) { setErroPost('Escolha o vídeo do Reels.'); setSalvandoPost(false); return; }
        body.video_url = (await enviarArquivo('/instagram/upload-video', 'video', videoArquivo)).video_url;
        if (capaArquivo) body.capa_url = (await enviarArquivo('/instagram/upload', 'imagem', capaArquivo)).imagem_url;
      } else {
        const url = await enviarImagemSeNecessario();
        if (!url) { setSalvandoPost(false); return; }
        body.imagem_url = url;
      }
      if (publicarAgora) body.publicar_agora = true;
      else if (agendarPara) body.agendado_para = new Date(agendarPara).toISOString();

      const { data } = await api.post('/instagram/posts', body);
      limparComposer();
      if (data?.na_fila) setAvisoPost('Reels na fila: o vídeo é processado pela Meta e publicado em 1 a 3 minutos. A lista atualiza sozinha.');
      carregarPosts();
    } catch (err) {
      setErroPost(err.response?.data?.erro || 'Erro ao salvar a postagem.');
    } finally { setSalvandoPost(false); }
  };

  const publicarAgoraPost = async (id) => {
    try {
      const { data } = await api.post(`/instagram/posts/${id}/publicar`);
      if (data?.na_fila) setAvisoPost('Reels na fila: publica em 1 a 3 minutos.');
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao publicar.');
    } finally {
      carregarPosts();
    }
  };

  const removerPost = async (id) => {
    if (!window.confirm('Remover esta postagem?')) return;
    try {
      await api.delete(`/instagram/posts/${id}`);
      carregarPosts();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao remover.');
    }
  };

  const FILTROS = [
    { valor: '', label: 'Todas' },
    { valor: 'rascunho', label: 'Rascunhos' },
    { valor: 'agendado', label: 'Agendadas' },
    { valor: 'publicado', label: 'Publicadas' },
    { valor: 'erro', label: 'Com erro' },
  ];

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>📷</span> Instagram</div>

      <div className={g.abasRow}>
        <button className={`${g.abaBtn} ${aba === 'postagens' ? g.abaBtnAtiva : ''}`} onClick={() => setAba('postagens')}>Postagens</button>
        <button className={`${g.abaBtn} ${aba === 'config' ? g.abaBtnAtiva : ''}`} onClick={() => setAba('config')}>Configuração</button>
      </div>

      {aba === 'config' ? (
        <div className={s.conteudo}>
          {!tokenConfigurado && (
            <div className={s.ajudaCampo} style={{ marginBottom: 10 }}>
              Ainda não configurado. Você vai precisar do <strong>ID da conta Instagram Business</strong> e de
              um <strong>Access Token</strong> gerados no Meta for Developers (developers.facebook.com) — a conta
              Instagram precisa ser tipo Empresa e estar vinculada a uma Página do Facebook.
            </div>
          )}
          <fieldset className={s.fieldset}>
            <legend className={s.legend}>Conta</legend>
            <label className={s.checkRow}>
              <input type="checkbox" checked={!!config.ativo} onChange={e => setConfig(c => ({ ...c, ativo: e.target.checked }))} />
              Publicação pelo sistema ativa
            </label>

            <div className={s.campo} style={{ marginTop: 10 }}>
              <span className={s.campoLabel}>ID da conta:</span>
              <input className={s.inputErp} style={{ width: 220 }} value={config.ig_user_id}
                onChange={e => setConfig(c => ({ ...c, ig_user_id: e.target.value }))}
                placeholder="17841400000000000" />
            </div>

            <div className={s.campo}>
              <span className={s.campoLabel}>Access Token:</span>
              <input className={s.inputErp} style={{ width: 320 }} type="password" value={accessToken}
                onChange={e => setAccessToken(e.target.value)}
                placeholder={tokenConfigurado ? '•••••••• (já configurado — deixe em branco pra manter)' : 'EAAxxxxxxxxxx...'} />
            </div>
            {config.username && (
              <p className={s.ajudaCampo}>Conta validada: <strong>@{config.username}</strong></p>
            )}
            <div className={s.campo}>
              <span className={s.campoLabel}>ID da Página do Facebook:</span>
              <input className={s.inputErp} style={{ width: 220 }} value={config.facebook_page_id || ''}
                onChange={e => setConfig(c => ({ ...c, facebook_page_id: e.target.value }))}
                placeholder="opcional: 1029384756" />
            </div>
            {config.facebook_page_nome && (
              <p className={s.ajudaCampo}>Página validada: <strong>{config.facebook_page_nome}</strong></p>
            )}
            <p className={s.ajudaCampo}>
              Com a Página preenchida, dá para publicar o Reels (ou a foto) também no Facebook. O token precisa
              das permissões <strong>instagram_content_publish</strong> e, para a Página, <strong>pages_manage_posts</strong>.
            </p>
            <p className={s.ajudaCampo}>
              Ao salvar, o sistema testa o token contra a conta informada antes de gravar — se algo estiver
              errado, o erro aparece na hora.
            </p>
          </fieldset>

          {erroConfig && <div className={s.erroMsg}>{erroConfig}</div>}
          {sucessoConfig && <div className={s.sucessoMsg}>{sucessoConfig}</div>}

          <div className={s.rodape} style={{ border: 'none', padding: '10px 0' }}>
            <button className={s.btnErpPrimario} onClick={salvarConfig} disabled={salvandoConfig || !config.ig_user_id}>
              {salvandoConfig ? 'Validando e salvando...' : '✔ Salvar'}
            </button>
          </div>
        </div>
      ) : (
        <div className={g.conteudoLargo}>
          <fieldset className={s.fieldset} style={{ maxWidth: 640 }}>
            <legend className={s.legend}>Nova Postagem</legend>
            <div className={g.abasRow} style={{ margin: '0 0 10px' }}>
              <button type="button" className={`${g.abaBtn} ${tipoMidia === 'imagem' ? g.abaBtnAtiva : ''}`} onClick={() => setTipoMidia('imagem')}>🖼️ Foto</button>
              <button type="button" className={`${g.abaBtn} ${tipoMidia === 'reels' ? g.abaBtnAtiva : ''}`} onClick={() => setTipoMidia('reels')}>🎬 Reels (vídeo)</button>
            </div>
            <div className={g.composerImgRow}>
              {tipoMidia === 'reels' ? (
                videoPreview ? (
                  <video className={g.composerPreview} src={videoPreview} controls muted playsInline style={{ aspectRatio: '9 / 16', objectFit: 'cover', background: '#000' }} />
                ) : (
                  <label className={g.composerPreviewVazio} style={{ cursor: 'pointer', aspectRatio: '9 / 16', height: 'auto' }}>
                    Escolher vídeo (MP4)
                    <input type="file" accept="video/mp4,video/quicktime" onChange={escolherVideo} style={{ display: 'none' }} />
                  </label>
                )
              ) : imagemPreview ? (
                <img className={g.composerPreview} src={imagemPreview} alt="Prévia" />
              ) : (
                <label className={g.composerPreviewVazio} style={{ cursor: 'pointer' }}>
                  Escolher imagem
                  <input type="file" accept="image/jpeg,image/png" onChange={escolherImagem} style={{ display: 'none' }} />
                </label>
              )}
              <div className={g.composerCampos}>
                {tipoMidia === 'imagem' && imagemPreview && (
                  <label style={{ fontSize: '0.75rem', color: 'var(--primary)', cursor: 'pointer', width: 'fit-content' }}>
                    Trocar imagem
                    <input type="file" accept="image/jpeg,image/png" onChange={escolherImagem} style={{ display: 'none' }} />
                  </label>
                )}
                {tipoMidia === 'reels' && videoPreview && (
                  <label style={{ fontSize: '0.75rem', color: 'var(--primary)', cursor: 'pointer', width: 'fit-content' }}>
                    Trocar vídeo
                    <input type="file" accept="video/mp4,video/quicktime" onChange={escolherVideo} style={{ display: 'none' }} />
                  </label>
                )}
                {tipoMidia === 'reels' && (
                  <label style={{ fontSize: '0.75rem', color: 'var(--primary)', cursor: 'pointer', width: 'fit-content' }}>
                    {capaArquivo ? `Capa: ${capaArquivo.name} (trocar)` : 'Capa do Reels (opcional, JPG)'}
                    <input type="file" accept="image/jpeg,image/png" onChange={e => setCapaArquivo(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                  </label>
                )}
                <textarea className={g.legendaInput} placeholder="Legenda (opcional)…"
                  value={legenda} onChange={e => setLegenda(e.target.value)} />
                <div style={{ display: 'flex', gap: 16, fontSize: '0.85rem' }}>
                  <label className={s.checkRow} style={{ margin: 0 }}>
                    <input type="checkbox" checked={noInstagram} onChange={e => setNoInstagram(e.target.checked)} /> Instagram
                  </label>
                  <label className={s.checkRow} style={{ margin: 0 }} title={config.facebook_page_id ? '' : 'Cadastre a Página do Facebook na aba Configuração'}>
                    <input type="checkbox" checked={noFacebook} disabled={!config.facebook_page_id}
                      onChange={e => setNoFacebook(e.target.checked)} /> Página do Facebook{config.facebook_page_nome ? ` (${config.facebook_page_nome})` : ''}
                  </label>
                </div>
                <div className={s.campo} style={{ margin: 0 }}>
                  <span className={s.campoLabel}>Agendar para:</span>
                  <input className={s.inputErp} style={{ width: 190 }} type="datetime-local"
                    value={agendarPara} onChange={e => setAgendarPara(e.target.value)} />
                </div>
              </div>
            </div>

            {erroPost && <div className={s.erroMsg}>{erroPost}</div>}
            {avisoPost && <div className={s.sucessoMsg}>{avisoPost}</div>}

            <div className={g.composerBotoes}>
              <button className={g.btnSecundario} disabled={salvandoPost || !(tipoMidia === 'reels' ? videoArquivo : imagemArquivo)} onClick={() => salvarPost(false)}>
                {agendarPara ? '🕒 Agendar' : '💾 Salvar rascunho'}
              </button>
              <button className={s.btnErpPrimario} disabled={salvandoPost || !(tipoMidia === 'reels' ? videoArquivo : imagemArquivo)} onClick={() => salvarPost(true)}>
                {salvandoPost ? 'Enviando...' : '📤 Publicar agora'}
              </button>
            </div>
          </fieldset>

          <div className={g.filtroRow}>
            {FILTROS.map(f => (
              <button key={f.valor} className={`${g.filtroBtn} ${filtroStatus === f.valor ? g.filtroBtnAtivo : ''}`}
                onClick={() => setFiltroStatus(f.valor)}>{f.label}</button>
            ))}
          </div>

          {posts.length === 0 ? (
            <div className={g.vazio}>Nenhuma postagem por aqui ainda.</div>
          ) : (
            <div className={g.gradePosts}>
              {posts.map(p => (
                <div key={p.id} className={g.postCard}>
                  {p.tipo_midia === 'reels' ? (
                    <video className={g.postFoto} src={`${urlImagem(p.video_url)}#t=1`} poster={p.capa_url ? urlImagem(p.capa_url) : undefined}
                      muted playsInline preload="metadata" controls style={{ objectFit: 'cover', background: '#000' }} />
                  ) : (
                    <img className={g.postFoto} src={urlImagem(p.imagem_url)} alt="" />
                  )}
                  <div className={g.postCorpo}>
                    <span className={g.statusBadge} style={{ background: COR_STATUS[p.status] }}>
                      {p.status === 'agendado' && new Date(p.agendado_para) <= new Date() ? 'Na fila…' : LABEL_STATUS[p.status]}
                    </span>
                    <div className={g.postMeta}>
                      {p.tipo_midia === 'reels' ? '🎬 Reels' : '🖼️ Foto'} ·{' '}
                      {[p.publicar_instagram && `Instagram${p.ig_media_id ? ' ✓' : ''}`, p.publicar_facebook && `Facebook${p.fb_post_id ? ' ✓' : ''}`].filter(Boolean).join(' + ')}
                    </div>
                    {p.legenda && <div className={g.postLegenda}>{p.legenda}</div>}
                    {p.status === 'agendado' && <div className={g.postMeta}>Agendado: {fmtData(p.agendado_para)}</div>}
                    {p.status === 'publicado' && <div className={g.postMeta}>Publicado: {fmtData(p.publicado_em)}</div>}
                    {p.erro_msg && <div className={g.postErro}>{p.erro_msg}</div>}
                    <div className={g.postAcoes}>
                      {p.ig_permalink && (
                        <a href={p.ig_permalink} target="_blank" rel="noreferrer">
                          <button type="button">Ver no Instagram</button>
                        </a>
                      )}
                      {p.fb_permalink && (
                        <a href={p.fb_permalink} target="_blank" rel="noreferrer">
                          <button type="button">Ver no Facebook</button>
                        </a>
                      )}
                      {p.status !== 'publicado' && p.status !== 'publicando' && (
                        <>
                          <button type="button" onClick={() => publicarAgoraPost(p.id)}>
                            {p.status === 'erro' ? 'Tentar de novo' : 'Publicar agora'}
                          </button>
                          {!p.ig_media_id && !p.fb_post_id && (
                            <button type="button" onClick={() => removerPost(p.id)}>Remover</button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
